import { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { Link, useSearchParams } from "react-router-dom";

import {
  authorizeGoogle,
  authorizeOutlook,
} from "../actionCreator/emailSetupActionCreater";
import { useAlert } from "../context/AlertProvider";
import { EmailContext } from "../context/EmailContext";
import {
  authorizeToGoogleForSignin,
  authorizeToGoogleForSignup,
  authorizeToMicrosoft365forSignin,
  authorizeToMicrosoft365forSignup,
} from "../actionCreator/socialAuthActionCreator";
import "../assets/css/auth.css";
import SignupForm from "../components/signup/SignupForm";
import { AuthContext } from "../context/AuthContext";
import { getAccountInfo } from "../actionCreator/commonActionCreator";
import Loader from "../components/loader";
import { authorizeGoogleRM } from "../actionCreator/reputationManagerActionCreator";
import { authorizeToSocial } from "../actionCreator/importActionCreator";
import { getToken } from "../utils/auth";
import { authorizeToCalendar } from "../actionCreator/calendarActionCreator";

import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";

const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

const AuthCallback = ({ type }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const code = searchParams.get("code");
  const { showAlert } = useAlert();
  const { refetch } = useContext(EmailContext);
  const { login } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [oauth2Token, setOauth2Token] = useState();
  const user = useSelector((state) => state.socialAuthReducer.user);

  useEffect(() => {
    const handleCallback = async () => {
      const token = getToken();
      setLoading(true);
      let res;
      switch (type) {
        case "gmail":
          res = await dispatch(authorizeGoogle(code));
          break;
        case "outlook":
          res = await dispatch(authorizeOutlook(code));
          break;
        case "signin-outlook":
          res = await dispatch(authorizeToMicrosoft365forSignin(code));
          break;
        case "signup-outlook":
          res = await dispatch(authorizeToMicrosoft365forSignup(code));
          break;
        case "signin-google":
          res = await dispatch(authorizeToGoogleForSignin(code));
          break;
        case "signup-google":
          res = await dispatch(authorizeToGoogleForSignup(code));
          break;
        case "reputation-manager-google":
          res = await dispatch(authorizeGoogleRM(code));
          break;
        case "gmail-import":
          res = await dispatch(
            authorizeToSocial({ type: "Gmail", code: code, token }),
          );
          break;
        case "outlook-import":
          res = await dispatch(
            authorizeToSocial({ type: "Outlook", code: code, token }),
          );
          break;
        case "google-calendar":
          res = await dispatch(
            authorizeToCalendar({ type: "Google", code: code, token }),
          );
          break;
        case "outlook-calendar":
          res = await dispatch(
            authorizeToCalendar({ type: "Outlook", code: code, token }),
          );
          break;
        default:
          throw new Error("Unsupported email provider");
      }

      if (res?.status === 200) {
        if (["gmail", "outlook"].includes(type)) {
          showAlert("success", `Your Email is Connected!`);
          refetch();
        }
        if (["gmail-import", "outlook-import"].includes(type)) {
          showAlert("success", `Your account is Synced!`);
          const returnTo = localStorage.getItem('oauth_return_to');
          if (returnTo) {
            localStorage.removeItem('oauth_return_to');
            navigate(returnTo);
          } else {
            navigate("/lead-import");
          }
        }
        if (["google-calendar", "outlook-calendar"].includes(type)) {
          showAlert("success", `Your calender is Synced!`);
          navigate("/leads-timeline/availability?tab=4");
        }
        if (
          ["signup-outlook", "signup-google"]?.includes(type) &&
          res?.data?.oauth2Token
        ) {
          setOauth2Token(res?.data?.oauth2Token);
        }
        if (res?.data?.token) {
          const user = await dispatch(getAccountInfo(res.data.token));
          login(res?.data?.token, true, user);
          navigate("/dashboard-view");
        }
      } else {
        if (["signup-outlook", "signup-google"]?.includes(type)) {
          navigate("/signup");
        }
        if (["gmail-import", "outlook-import"].includes(type)) {
          const returnTo = localStorage.getItem('oauth_return_to');
          if (returnTo) {
            localStorage.removeItem('oauth_return_to');
            navigate(returnTo);
          } else {
            navigate("/lead-import");
          }
        }
        if (["google-calendar", "outlook-calendar"].includes(type)) {
          navigate("/leads-timeline/availability?tab=4");
        }
        if (
          res?.response?.status === 401 &&
          ["signin-outlook", "signin-google"]?.includes(type)
        ) {
          showAlert(
            "error",
            <b>Oops!</b>,
            <>
              <div>It appears you need to create an account first.</div>
              <div>
                👉 Please{" "}
                <Link to="/signup" className="text-theme">
                  sign up
                </Link>{" "}
                before attempting to log in.
              </div>
            </>,
          );
        } else {
          if (["signup-outlook", "signup-google"]?.includes(type)) {
            showAlert(
              "error",
              "We couldn't complete your registration because the authorization code has expired. Please sign up again.",
            );
          } else {
            if (["gmail-import", "outlook-import"].includes(type)) {
              if (res?.response?.status === 400) {
                showAlert("error", res?.response?.data?.title);
              } else {
                showAlert("error", `Something went wrong. Please try again.`);
              }
            }
            if (["google-calendar", "outlook-calendar"].includes(type)) {
              if (res?.response?.status === 400) {
                showAlert("error", res?.response?.data?.title);
              } else {
                showAlert("error", `Something went wrong. Please try again.`);
              }
            }
          }
        }
        if (["signin-outlook", "signin-google"]?.includes(type)) {
          navigate("/signin");
        }
      }
      setLoading(false);
    };
    handleCallback();
    if (["gmail", "outlook"].includes(type)) {
      navigate("/settings/email-setup");
    }
    if (["reputation-manager-google"].includes(type)) {
      showAlert("success", `Your Account is Connected!`);
      navigate("/reputation-manager/settings");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, dispatch, code, type]);

  return (
    <>
      {["signup-outlook", "signup-google"]?.includes(type) ? (
        <div className="auth">
          <div className="social-auth d-flex align-items-center justify-content-center min-vh-100 h-100">
            <div className="login-card" style={{ maxWidth: 555, margin: 20 }}>
              <div className="heading24px text-color-030334 fw-semibold text-center">
                Congratulations!
              </div>

              <div className="heading16px text-color-414141 text-center mt10">
                You’re just one step away from unlocking the full potential of
                us!
              </div>
              <div className="py-20 mt10 border-top">
                {siteKey ? (
                  <GoogleReCaptchaProvider reCaptchaKey={siteKey}>
                    <SignupForm
                      socialAuth
                      user={user}
                      type={type}
                      oauth2Token={oauth2Token}
                    />
                  </GoogleReCaptchaProvider>
                ) : (
                  <SignupForm
                    socialAuth
                    user={user}
                    type={type}
                    oauth2Token={oauth2Token}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <> {loading && <Loader />}</>
      )}
    </>
  );
};

export default AuthCallback;
